import React from 'react';
import { isRootOrSuperRole } from '../DashboardAccess';
import type { DashboardRole } from '../interfaces';
import { Theme } from '../Theme';
import {
    formatStripeInvoiceAmount,
    getProviderPaymentSummary,
    ProviderPaidInvoice,
} from '../saas/ProviderPaymentApi';
import { Button } from './Button';
import '../saas/PlanCatalogAdmin.css';

type Props = {
    healthcareProviderId: string;
    userId: string;
    dashboardRole: DashboardRole;
};

type PaymentState =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'loaded'; payment: ProviderPaidInvoice | null };

// Mount private billing data only for the effective Root/Super role, never billing ownership.
export function ProviderPaymentSummary({ healthcareProviderId, userId, dashboardRole }: Props) {
    if (!healthcareProviderId || !isRootOrSuperRole(dashboardRole)) return null;

    // Remount on every access-scope change so no previous organisation's amount can flash.
    return <PaidInvoiceContent
        key={JSON.stringify([healthcareProviderId, userId, dashboardRole])}
        healthcareProviderId={healthcareProviderId}
    />;
}

// Keep each request local to its mounted scope and ignore late completions after navigation.
function PaidInvoiceContent({ healthcareProviderId }: { healthcareProviderId: string }) {
    const [state, setState] = React.useState<PaymentState>({ status: 'loading' });
    const [requestNumber, setRequestNumber] = React.useState(0);

    React.useEffect(() => {
        let active = true;
        setState({ status: 'loading' });
        void getProviderPaymentSummary(healthcareProviderId).then((summary) => {
            if (active) setState({ status: 'loaded', payment: summary.payment });
        }).catch(() => {
            // Do not expose raw Stripe or transport errors in the organisation UI.
            if (active) setState({ status: 'error' });
        });
        return () => { active = false; };
    }, [healthcareProviderId, requestNumber]);

    const payment = state.status === 'loaded' ? state.payment : null;
    return (
        <section aria-labelledby='last-paid-invoice-heading' style={styles.section}>
            {/* Reuse the same privileged-section tag as the Tools page. */}
            <header style={styles.header}>
                <span className='catalog-admin__root-badge'>Root &amp; Super</span>
                <h3 id='last-paid-invoice-heading' style={styles.heading}>Last paid invoice</h3>
            </header>
            {state.status === 'loading' && <p role='status' style={styles.note}>Loading payment details…</p>}
            {state.status === 'error' && <>
                <p role='alert' style={styles.note}>Payment details could not be loaded. Please try again.</p>
                <div><Button
                    type='secondary'
                    label='Retry payment details'
                    onClick={() => setRequestNumber((previous) => previous + 1)}
                /></div>
            </>}
            {state.status === 'loaded' && !payment && <p style={styles.note}>
                No paid Stripe invoice was found for this organisation.
            </p>}
            {payment && <>
                {!payment.livemode && <p style={styles.note}>Stripe test-mode invoice — not a live payment.</p>}
                <dl style={styles.grid}>
                    <div style={styles.item}>
                        <dt style={styles.label}>Amount paid</dt>
                        <dd style={styles.amount}>{formatStripeInvoiceAmount(payment.amountPaid, payment.currency)}</dd>
                    </div>
                    {payment.discountAmount > 0 && <div style={styles.item}>
                        <dt style={styles.label}>Discount applied</dt>
                        <dd style={styles.value}>{formatStripeInvoiceAmount(payment.discountAmount, payment.currency)}</dd>
                    </div>}
                    {payment.taxAmount !== null && <div style={styles.item}>
                        <dt style={styles.label}>Invoice tax</dt>
                        <dd style={styles.value}>{formatStripeInvoiceAmount(payment.taxAmount, payment.currency)}</dd>
                    </div>}
                    <div style={styles.item}>
                        <dt style={styles.label}>Invoice total</dt>
                        <dd style={styles.value}>{formatStripeInvoiceAmount(payment.total, payment.currency)}</dd>
                    </div>
                    {payment.paidAt && <div style={styles.item}>
                        <dt style={styles.label}>Paid on</dt>
                        <dd style={styles.value}>{new Date(payment.paidAt).toLocaleDateString('en-GB', { timeZone: 'UTC' })}</dd>
                    </div>}
                </dl>
                <p style={styles.note}>
                    Actual amount recorded by Stripe after discounts for this past invoice, not a renewal quote.
                    Credits can affect the amount paid; subsequent refunds are not deducted here.
                </p>
            </>}
        </section>
    );
}

// Match the existing subscription details card without changing shared organisation styling.
const styles = {
    section: {
        ...Theme.Fonts.body,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
        padding: '18px',
        backgroundColor: Theme.Colours.workArea,
        border: Theme.Colours.border,
        borderRadius: '10px',
    },
    header: { display: 'flex', flexDirection: 'column' as const, alignItems: 'flex-start', gap: '8px' },
    heading: { ...Theme.Fonts.body, fontWeight: 700, margin: '0 0 4px 0' },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px 24px',
        margin: 0,
    },
    item: { display: 'flex', flexDirection: 'column' as const, gap: '5px', minWidth: 0 },
    label: { opacity: 0.72 },
    value: { margin: 0, fontWeight: 600, overflowWrap: 'anywhere' as const },
    amount: { margin: 0, fontWeight: 700, fontSize: '20px', overflowWrap: 'anywhere' as const },
    note: { margin: 0, opacity: 0.72 },
};
